import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Image as ImageIcon, UserCircle } from "lucide-react";
import { CABINET, EXPERIENCE, COMPETENCES } from "@/lib/content";

export default function About() {
    return (
        <main data-testid="about-page" style={{ paddingTop: 140 }}>
            {/* Header */}
            <section style={{ padding: "40px 40px 60px" }}>
                <div className="mx-auto" style={{ maxWidth: 1400 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
                        className="grid grid-cols-1 md:grid-cols-12 gap-16 items-end"
                    >
                        <div className="md:col-span-8">
                            <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                                Le cabinet · À propos
                            </div>
                            <div className="divider-terra mt-3" />
                            <h1
                                className="font-display mt-8"
                                style={{
                                    fontSize: "clamp(3rem, 7vw, 6.5rem)",
                                    lineHeight: 0.95,
                                    color: "var(--forest-dark)",
                                    fontWeight: 400,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                Vingt-six ans
                                <br />
                                <span style={{ fontStyle: "italic", color: "var(--terra)" }}>
                                    au chevet
                                </span>{" "}
                                du souffle
                                <br />et du mouvement.
                            </h1>
                        </div>
                        <div className="md:col-span-4">
                            <p
                                className="font-display"
                                style={{
                                    fontSize: "1.15rem",
                                    lineHeight: 1.65,
                                    color: "var(--ink-mid)",
                                    fontStyle: "italic",
                                }}
                            >
                                Un cabinet de proximité à Bex, dans le Chablais vaudois. Une
                                pratique documentée, des comptes-rendus systématiques, une
                                coordination active avec le corps médical de la région.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Photo du cabinet + Portrait Ana — placeholders en attente des vraies photos */}
            <section style={{ padding: "40px 40px 80px" }}>
                <div className="mx-auto grid grid-cols-1 md:grid-cols-12 gap-8" style={{ maxWidth: 1400 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9 }}
                        className="md:col-span-7"
                        style={{
                            position: "relative",
                            aspectRatio: "4 / 3",
                            overflow: "hidden",
                            borderRadius: 4,
                            background: "linear-gradient(135deg, var(--cream-warm) 0%, var(--terra-light) 100%)",
                            border: "1px solid var(--hairline)",
                        }}
                        data-testid="about-photo-cabinet-placeholder"
                    >
                        <div aria-hidden className="breathe-slow" style={{ position: "absolute", right: -140, top: -140, width: 420, height: 420, borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.35)" }} />
                        <div aria-hidden className="breathe" style={{ position: "absolute", right: -60, top: -60, width: 260, height: 260, borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.2)" }} />
                        <div style={{
                            position: "absolute", inset: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexDirection: "column", gap: 12, color: "var(--forest)",
                        }}>
                            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <ImageIcon size={20} style={{ color: "var(--terra-deep)" }} />
                            </div>
                            <div className="font-mono-clinical" style={{ color: "var(--terra-deep)" }}>
                                Photo du cabinet
                            </div>
                            <div style={{ fontSize: 12, color: "var(--ink-soft)", fontStyle: "italic", letterSpacing: "0.06em" }}>
                                À venir · ouverture décembre 2026
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                        className="md:col-span-5"
                    >
                        <div style={{
                            background: "var(--cream-warm)",
                            border: "1px solid var(--hairline)",
                            padding: "40px 32px",
                            height: "100%",
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: 4,
                        }}>
                            <div aria-hidden className="breathe" style={{ position: "absolute", right: -80, top: -80, width: 240, height: 240, borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.25)" }} />

                            {/* Portrait placeholder — cercle */}
                            <div
                                data-testid="about-portrait-placeholder"
                                style={{
                                    width: 96, height: 96, borderRadius: "50%",
                                    background: "linear-gradient(135deg, var(--terra-light) 0%, var(--terra) 100%)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#fff", position: "relative", marginBottom: 20,
                                }}
                            >
                                <UserCircle size={40} strokeWidth={1.2} />
                            </div>

                            <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                                Physiothérapeute diplômée
                            </div>
                            <div className="mt-3" style={{ height: 1, width: 40, background: "var(--terra)" }} />
                            <div className="font-display mt-6" style={{ fontSize: "2rem", fontStyle: "italic", color: "var(--forest-dark)", lineHeight: 1.1 }}>
                                Ana Lourenço
                            </div>
                            <p className="font-display mt-4" style={{ fontSize: "1rem", lineHeight: 1.6, color: "var(--ink-mid)" }}>
                                Diplômée en physiothérapie, vingt-six ans d'exercice
                                clinique. Formation continue régulière en
                                cardio-respiratoire et périnéologie, praticienne formatrice.
                            </p>
                            <div className="mt-6 flex items-center gap-3" style={{ color: "var(--terra-deep)", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.15em" }}>
                                <MapPin size={13} />
                                Rue Centrale 27, 1880 Bex
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Experience blocks */}
            <section style={{ padding: "80px 40px" }}>
                <div className="mx-auto grid grid-cols-1 md:grid-cols-12 gap-16" style={{ maxWidth: 1400 }}>
                    <div className="md:col-span-4">
                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                            Parcours professionnel
                        </div>
                        <div className="divider-terra mt-3" />
                        <h2 className="font-display mt-8" style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", lineHeight: 1, color: "var(--forest-dark)", fontWeight: 400 }}>
                            De l'aigu à la <span style={{ fontStyle: "italic", color: "var(--terra)" }}>durée</span>.
                        </h2>
                        <p className="font-display mt-6" style={{ fontSize: "1.05rem", lineHeight: 1.6, color: "var(--ink-mid)", fontStyle: "italic", maxWidth: 340 }}>
                            Quatre versants d'une même pratique — hospitalière, ambulatoire, formatrice, coordinatrice.
                        </p>
                    </div>

                    <div className="md:col-span-8">
                        <ul style={{ listStyle: "none", padding: 0 }}>
                            {EXPERIENCE.map((exp, i) => (
                                <motion.li
                                    key={exp.heading}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.6, delay: i * 0.08 }}
                                    className="grid grid-cols-12 gap-6 items-start py-8"
                                    style={{
                                        borderTop: i === 0 ? "1px solid var(--hairline)" : "none",
                                        borderBottom: "1px solid var(--hairline)",
                                    }}
                                >
                                    <div className="col-span-2 md:col-span-1 font-display" style={{ fontSize: 22, color: "var(--terra)", fontStyle: "italic" }}>
                                        0{i + 1}
                                    </div>
                                    <div className="col-span-10 md:col-span-11">
                                        <div className="font-display" style={{ fontSize: "clamp(1.3rem, 2vw, 1.8rem)", color: "var(--forest-dark)", lineHeight: 1.15, marginBottom: 8 }}>
                                            {exp.heading}
                                        </div>
                                        <div style={{ fontSize: 15, color: "var(--ink-mid)", lineHeight: 1.65, maxWidth: 640 }}>
                                            {exp.body}
                                        </div>
                                    </div>
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Cabinet gallery — retirée : photos non disponibles */}

            {/* Compétences */}
            <section style={{ padding: "80px 40px 120px" }}>
                <div className="mx-auto grid grid-cols-1 md:grid-cols-12 gap-16" style={{ maxWidth: 1400 }}>
                    <div className="md:col-span-5">
                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                            Compétences cliniques
                        </div>
                        <div className="divider-terra mt-3" />
                        <h2 className="font-display mt-8" style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)", lineHeight: 1, color: "var(--forest-dark)", fontWeight: 400 }}>
                            Une boîte à outils <span style={{ fontStyle: "italic", color: "var(--terra)" }}>large</span>,
                            <br />mobilisée avec discernement.
                        </h2>
                        <Link to="/rendez-vous" data-testid="about-cta-appointment" className="btn-primary mt-10 inline-flex">
                            Adresser un patient <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="md:col-span-7">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ listStyle: "none", padding: 0 }}>
                            {COMPETENCES.map((c, i) => (
                                <motion.li
                                    key={c}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.5, delay: i * 0.05 }}
                                    className="card-clinical flex items-center gap-4"
                                    style={{ padding: "20px 24px" }}
                                >
                                    <span className="font-display" style={{ fontSize: 20, color: "var(--terra)", fontStyle: "italic", minWidth: 30 }}>
                                        0{i + 1}
                                    </span>
                                    <span style={{ fontSize: 15, color: "var(--forest-dark)", fontWeight: 500 }}>{c}</span>
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>
        </main>
    );
}
